import { ModelText as ModelTextCodec } from '@cadstrom/cadstrom-shared';
import { Fragment } from 'react';

import { useModelTextDialects } from './use-model-text-dialects.js';

export interface ModelTextProps {
	value: string;
	dialects?: ModelTextCodec.TextDialect[];
	declarations?: ModelTextCodec.TextDeclarations;
	className?: string;
}

/**
 * Renders a raw model identifier using the CAD text conventions active for the current project.
 *
 * @param props - Raw identifier and optional display-convention overrides.
 * @returns Display text with overbar runs represented by inline spans.
 */
export function ModelText({ value, dialects, declarations, className }: ModelTextProps) {
	const contextDialects = useModelTextDialects();
	const display = ModelTextCodec.resolveDisplayText({
		raw: value,
		dialects: dialects ?? contextDialects,
		declarations,
	});
	const bars = display.transforms.filter((transform) => transform.type === 'bar');

	return (
		<span className={className}>
			{bars.map((bar, index) => {
				const previousEnd = bars[index - 1]?.to ?? 0;
				return (
					<Fragment key={`run-${bar.from}-${bar.to}`}>
						{previousEnd < bar.from && <span>{display.text.slice(previousEnd, bar.from)}</span>}
						{/* The bar is an absolutely positioned pseudo-element: it adds no layout size and
					    anchors to the inline box's font-derived content area, independent of line-height.
					    text-decoration overline cannot do this — it sits at the font's overline metric
					    (overlapping glyphs here) and has no offset control. */}
						<span className="relative after:absolute after:inset-x-0 after:top-[-0.18em] after:border-t-[0.10em] after:border-current after:content-['']">
							{display.text.slice(bar.from, bar.to)}
						</span>
					</Fragment>
				);
			})}
			{bars.length > 0 && bars.at(-1)?.to !== display.text.length && <span>{display.text.slice(bars.at(-1)?.to)}</span>}
			{bars.length === 0 && display.text}
		</span>
	);
}
